import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

import {
  createChatSession,
  fetchChatLoadingMessages,
  isChatMessageTooLong,
  isDefaultShopperSessionTitle,
  listChatMessages,
  listChatSessions,
  MAX_CHAT_MESSAGE_CHARS,
  postChatMessage,
  titleFromPrompt,
} from '@/api/widget/chat'
import {
  type ChatStreamConnection,
  type ChatStreamEvent,
  errorFromFailedStreamEvent,
  openChatStream,
} from '@/api/widget/chatStream'
import { widgetBridge } from '@/bridge'
import { WidgetStep } from '@/types'
import type { ChatMessage, ChatSession, ChatStreamHandle } from '@/types/widget'
import { logger } from '@/utils'
import { track } from '@/utils/analytics'
import { createPersistOptions } from '@/utils/zustand-persist'
import { useFlowState } from './useFlowState'
import { getContactID, getCurrentGlobalID, getCurrentUID } from './useSessionStore'
import { getStoreIDByGlobalID } from './useLocationsStore'
import { useWidgetStore } from './useWidgetStore'

const POLL_INTERVAL_MS = 3000
const IDLE_POLL_INTERVAL_MS = 6000
const IDLE_POLL_AFTER = 20
const MESSAGES_LIMIT = 100
/** Cap preview prefetch so opening recent chats doesn't fan out dozens of GETs. */
const SESSION_PREVIEW_PREFETCH_LIMIT = 12
const SESSION_PREVIEW_CONCURRENCY = 3
/** Stop the typing wait if the API never returns a contentful assistant reply. */
const AWAITING_REPLY_TIMEOUT_MS = 90_000
/** Client id prefix for the transient assistant bubble populated by stream deltas. */
const STREAM_PLACEHOLDER_ID_PREFIX = 'local-stream-'

const isStreamPlaceholderMessage = (message: ChatMessage): boolean =>
  typeof message.id === 'string' && message.id.startsWith(STREAM_PLACEHOLDER_ID_PREFIX)

type ChatStoreState = {
  sessionId: string | undefined
  messages: ChatMessage[]
  input: string
  sending: boolean
  polling: boolean
  pollCount: number
  lastAssistantAt: number | undefined
  /**
   * Explicit wait flag for the typing indicator. Set on send; cleared only when
   * a contentful assistant reply arrives (or on error / timeout / reset). Empty
   * assistant placeholders are ignored for this flag because ChatScreen hides
   * them. Do not derive from last-message role alone — post-send polls can
   * briefly return the previous assistant as last.
   */
  awaitingReply: boolean
  error: string | undefined
  /**
   * Loading messages shown by the TypingIndicator while awaiting an assistant
   * reply. Populated per-prompt via fetchChatLoadingMessages; falls back to
   * the WidgetChatScreen settings default when the backend endpoint is not
   * available or returns nothing useful.
   */
  loadingMessages: string[] | undefined
  /**
   * Recent chat sessions for the current merchant, sorted with most-recent
   * first. Populated lazily via loadSessions() when the history drawer is
   * opened. Not persisted — always refreshed from the server.
   */
  sessions: ChatSession[]
  sessionsLoading: boolean
  sessionsError: string | undefined
  /**
   * Lightweight message previews keyed by session id, used by the history
   * drawer to label chats from the first user prompt (dash RecentPrompts pattern).
   */
  sessionPreviews: Record<string, ChatMessage[]>
  /** True while the active session's messages are being fetched into an empty thread. */
  messagesLoading: boolean
  /**
   * Set by newChat so remounting ChatScreen (or reopening the widget) keeps the
   * empty draft instead of auto-resuming the latest server session. Cleared when
   * a session is selected or created.
   */
  preferNewChat: boolean
  /**
   * Client id (`local-stream-*`) of the placeholder assistant message being
   * populated by WebSocket token deltas. Cleared when the stream finalizes and
   * the canonical server message replaces it. Stripped from persistence so a
   * reload can't resurrect a partial reply.
   */
  streamingAssistantId: string | undefined
  /**
   * Highest token sequence applied so far for the current stream. The broker
   * replays buffered history on subscribe (astrobotstream Subscribe replays
   * from `history`), so deltas can repeat — this guards against double-append.
   */
  streamingSequence: number
  /**
   * True while a job-scoped stream socket is open and can accept a cancel
   * frame. Distinct from awaitingReply: the first token clears the typing
   * indicator but cancel must stay available until the job ends.
   */
  canCancelReply: boolean
}

type ChatStoreActions = {
  setInput: (input: string) => void
  ensureSession: () => Promise<string | undefined>
  loadMessages: () => Promise<ChatMessage[] | undefined>
  sendMessage: (text?: string) => Promise<void>
  /** Re-send a failed optimistic user message by id. */
  retryFailedMessage: (messageId: string) => Promise<void>
  requestLoadingMessages: (prompt: string) => void
  loadSessions: () => Promise<void>
  loadSessionPreviews: (sessionIds: string[]) => Promise<void>
  selectSession: (sessionId: string) => Promise<void>
  /**
   * When entering chat with no active thread, resume the most recent server
   * session — unless the user explicitly started a new chat.
   */
  resumeLatestSession: () => Promise<void>
  newChat: () => void
  startPolling: () => void
  stopPolling: () => void
  /** Close the active stream socket and drop its in-memory placeholder. */
  stopStreaming: () => void
  /**
   * Ask the broker to cancel the in-flight job. Does not close the widget
   * or the socket; the terminal `cancelled` event (or a drop after cancel)
   * clears local wait state.
   */
  cancelStreaming: () => void
  /**
   * Open a WebSocket to the astrobot stream broker and feed token deltas
   * into the current assistant bubble. Called from sendMessage when the POST
   * response includes a stream handle. On any failure the client silently
   * falls back to the existing HTTP polling path.
   */
  startStream: (stream: ChatStreamHandle, sendEpoch: number) => void
  reset: () => void
}

type ChatStore = ChatStoreState & ChatStoreActions

const initialState: ChatStoreState = {
  sessionId: undefined,
  messages: [],
  input: '',
  sending: false,
  polling: false,
  pollCount: 0,
  lastAssistantAt: undefined,
  awaitingReply: false,
  error: undefined,
  loadingMessages: undefined,
  sessions: [],
  sessionsLoading: false,
  sessionsError: undefined,
  sessionPreviews: {},
  messagesLoading: false,
  preferNewChat: false,
  streamingAssistantId: undefined,
  streamingSequence: 0,
  canCancelReply: false,
}

let pollTimer: ReturnType<typeof setTimeout> | null = null
let pollUnsubscribers: Array<() => void> = []
// Tracks the most recent prompt so a slow /loading-messages response from an
// earlier send doesn't clobber a newer one.
let activeLoadingPrompt: string | null = null
// Bumped on newChat / selectSession / reset so in-flight sendMessage work
// can detect that the active thread changed and abort without rebinding state.
let threadEpoch = 0
// Shared so sendMessage can wait out mount-time resume before creating a session.
let resumeInFlight: Promise<void> | null = null
// Single active WebSocket connection to the astrobot stream broker. Closed
// on thread switch, widget close, reset, or when the broker publishes a
// terminal completed/failed event.
let streamConnection: ChatStreamConnection | null = null
let streamUnsubscribers: Array<() => void> = []
// Bumped when a new stream starts or streaming is torn down so an in-flight
// finalize from a prior socket cannot clobber the current turn.
let streamEpoch = 0
// Watchdog for a hung WebSocket. Cleared on finalize / teardown; reset on
// stream events so an active generation is not cut off.
let streamWatchdog: ReturnType<typeof setTimeout> | null = null

const clearStreamWatchdog = () => {
  if (streamWatchdog) {
    clearTimeout(streamWatchdog)
    streamWatchdog = null
  }
}

const bumpThreadEpoch = () => {
  threadEpoch += 1
}

const clearPollSubscriptions = () => {
  for (const unsub of pollUnsubscribers) unsub()
  pollUnsubscribers = []
}

const clearStreamSubscriptions = () => {
  for (const unsub of streamUnsubscribers) unsub()
  streamUnsubscribers = []
}

const closeStreamConnection = () => {
  clearStreamWatchdog()
  clearStreamSubscriptions()
  if (streamConnection) {
    streamConnection.close()
    streamConnection = null
  }
}

const stopStreaming = () => {
  closeStreamConnection()
  streamEpoch += 1
  // Drop the in-memory placeholder so a widget-close mid-stream doesn't leave
  // partial-reply text hanging around beside the eventual server message.
  // Persistence separately filters placeholders on the way to storage.
  const state = useChatStore.getState()
  if (state.streamingAssistantId || state.canCancelReply) {
    const placeholderId = state.streamingAssistantId
    useChatStore.setState({
      messages: placeholderId ? state.messages.filter((m) => m.id !== placeholderId) : state.messages,
      streamingAssistantId: undefined,
      streamingSequence: 0,
      canCancelReply: false,
    })
  }
}

const isReplyInFlight = (state: { sending: boolean; awaitingReply: boolean; canCancelReply: boolean }) =>
  state.sending || state.awaitingReply || state.canCancelReply

const mapPool = async <T, R>(
  items: T[],
  concurrency: number,
  mapper: (item: T) => Promise<R>
): Promise<R[]> => {
  if (items.length === 0) return []
  const results: R[] = new Array(items.length)
  let nextIndex = 0

  const workers = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (nextIndex < items.length) {
      const index = nextIndex
      nextIndex += 1
      results[index] = await mapper(items[index])
    }
  })

  await Promise.all(workers)
  return results
}

const isValidSessionId = (sessionId: string | undefined): sessionId is string =>
  typeof sessionId === 'string' && sessionId.trim() !== '' && sessionId !== 'undefined' && sessionId !== 'null'

/**
 * Store-side messages in the shopper widget. Inbox staff replies persist as
 * `merchant`; AI replies are `assistant` or `system`. Shopper turns may use
 * `user`, `shopper`, `customer`, or `human`, so we identify store-side roles
 * positively and treat everything else as the shopper.
 */
export function isAssistantMessage(message?: ChatMessage): boolean {
  const role = message?.role?.toLowerCase()
  return role === 'assistant' || role === 'system' || role === 'merchant'
}

/** True when a store-side message has visible reply content (not a placeholder). */
export function hasAssistantReplyContent(message?: ChatMessage): boolean {
  return isAssistantMessage(message) && Boolean(message?.content?.trim())
}

const messagesEqual = (a: ChatMessage[], b: ChatMessage[]): boolean => {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    const av = a[i]
    const bv = b[i]
    if (av.role !== bv.role || av.content !== bv.content || av.id !== bv.id) return false
  }
  return true
}

const findLastContentfulAssistantIndex = (messages: ChatMessage[]): number => {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (hasAssistantReplyContent(messages[i])) return i
  }
  return -1
}

const hasNewContentfulAssistant = (prev: ChatMessage[], next: ChatMessage[]): boolean => {
  const prevIdx = findLastContentfulAssistantIndex(prev)
  const nextIdx = findLastContentfulAssistantIndex(next)
  return (
    nextIdx > prevIdx || (nextIdx >= 0 && prevIdx >= 0 && next[nextIdx].content !== prev[prevIdx]?.content)
  )
}

/** True when the latest user turn already has a contentful assistant reply. */
const hasAssistantReplyAfterLastUser = (messages: ChatMessage[]): boolean => {
  let lastUserIdx = -1
  for (let i = messages.length - 1; i >= 0; i--) {
    if (!isAssistantMessage(messages[i])) {
      lastUserIdx = i
      break
    }
  }
  for (let i = lastUserIdx + 1; i < messages.length; i++) {
    if (hasAssistantReplyContent(messages[i])) return true
  }
  return false
}

const isUserMessage = (message?: ChatMessage): message is ChatMessage => {
  if (!message) return false
  return !isAssistantMessage(message) && !isStreamPlaceholderMessage(message)
}

/** True when `next` already has a contentful assistant for the placeholder's turn. */
const serverHasReplyForPlaceholder = (
  placeholder: ChatMessage,
  prev: ChatMessage[],
  next: ChatMessage[]
): boolean => {
  const placeholderIdx = prev.findIndex((m) => m.id === placeholder.id)
  let lastUserContent: string | undefined
  for (let i = placeholderIdx - 1; i >= 0; i--) {
    if (isUserMessage(prev[i])) {
      lastUserContent = prev[i].content
      break
    }
  }

  if (lastUserContent === undefined) {
    return hasAssistantReplyAfterLastUser(next)
  }

  // Match the Nth same-text user turn, not the last one. A repeated prompt
  // otherwise maps every placeholder onto the newest user message.
  let ordinal = 0
  for (let i = 0; i < placeholderIdx; i++) {
    if (isUserMessage(prev[i]) && prev[i].content === lastUserContent) {
      ordinal += 1
    }
  }

  let seen = 0
  let userIdx = -1
  for (let i = 0; i < next.length; i++) {
    if (isUserMessage(next[i]) && next[i].content === lastUserContent) {
      seen += 1
      if (seen === ordinal) {
        userIdx = i
        break
      }
    }
  }
  if (userIdx < 0) return false

  for (let i = userIdx + 1; i < next.length; i++) {
    if (isUserMessage(next[i])) break
    if (hasAssistantReplyContent(next[i])) return true
  }
  return false
}

const emitChatReplyReceived = (sessionId: string, content: string | undefined, latencyMs: number) => {
  const replyGlobalID = getCurrentGlobalID()
  const replyStoreID = replyGlobalID ? getStoreIDByGlobalID(replyGlobalID) : undefined
  const replyContactID = getContactID()
  widgetBridge.emit('chat:reply_received', {
    sessionId,
    content,
    latencyMs,
    storeID: replyStoreID,
    contactID: replyContactID,
  })
  track('chat', 'chat_reply_received', {
    latencyMs,
    hasStoreID: Boolean(replyStoreID),
    hasContactID: Boolean(replyContactID),
  })
}

/** True while the chat screen is on-screen and should keep polling for replies. */
export const isChatUiActive = (): boolean => {
  if (useFlowState.getState().currentStep !== WidgetStep.Chat) return false
  const { isFloatingWidgetMode, floatingWidgetOpen } = useWidgetStore.getState()
  return !isFloatingWidgetMode || floatingWidgetOpen
}

const isOptimisticMessage = (message: ChatMessage): boolean =>
  typeof message.id === 'string' && message.id.startsWith('local-')

/**
 * Keep optimistic user bubbles until the server list includes a matching
 * user message. Prevents the post-send fetch from wiping a message that
 * has not been persisted yet.
 */
const reconcileMessages = (prev: ChatMessage[], next: ChatMessage[]): ChatMessage[] => {
  const optimistic = prev.filter(isOptimisticMessage)
  if (optimistic.length === 0) return next

  const claimed = new Set<number>()
  const pending: ChatMessage[] = []

  for (const opt of optimistic) {
    if (isStreamPlaceholderMessage(opt)) {
      // Keep each streamed bubble until the server list includes a reply
      // for that turn. A prior turn's reply must not drop a newer stream.
      if (!serverHasReplyForPlaceholder(opt, prev, next)) {
        pending.push(opt)
      }
      continue
    }
    const matchIdx = next.findIndex((m, i) => {
      if (claimed.has(i) || isAssistantMessage(m)) return false
      return m.content === opt.content
    })
    if (matchIdx >= 0) {
      claimed.add(matchIdx)
    } else {
      pending.push(opt)
    }
  }

  return pending.length === 0 ? next : [...next, ...pending]
}

export const useChatStore = create<ChatStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        setInput: (input) => set({ input }, false, 'setInput'),

        ensureSession: async () => {
          const existing = get().sessionId
          if (isValidSessionId(existing)) return existing

          const uid = getCurrentUID()
          if (!uid) {
            set({ error: 'No current UID' }, false, 'ensureSession/noUID')
            return undefined
          }

          // Pass any known identity hints so the backend can associate the
          // session with the right store/contact from the first request.
          const initialGlobalID = getCurrentGlobalID()
          const initialStoreID = initialGlobalID ? getStoreIDByGlobalID(initialGlobalID) : undefined
          const initialContactID = getContactID()

          const epoch = threadEpoch
          const [session, error] = await createChatSession(uid, {
            storeID: initialStoreID,
            contactID: initialContactID,
          })
          if (epoch !== threadEpoch) {
            return undefined
          }
          if (error || !session?.id) {
            logger.error('ensureSession', error)
            set({ error: error ?? 'Failed to create chat session' }, false, 'ensureSession/error')
            return undefined
          }

          set(
            { sessionId: session.id, preferNewChat: false, error: undefined },
            false,
            'ensureSession/created'
          )
          logger.log('ensureSession/created', session.id)
          widgetBridge.emit('chat:session_created', {
            sessionId: session.id,
            storeID: initialStoreID,
            contactID: initialContactID,
          })
          track('chat', 'chat_session_created', {
            sessionId: session.id,
            hasStoreID: Boolean(initialStoreID),
            hasContactID: Boolean(initialContactID),
          })
          return session.id
        },

        loadMessages: async () => {
          const sessionId = get().sessionId
          if (!isValidSessionId(sessionId)) {
            set({ messages: [], messagesLoading: false }, false, 'loadMessages/noSession')
            return []
          }

          const uid = getCurrentUID()
          if (!uid) {
            set({ messagesLoading: false }, false, 'loadMessages/noUID')
            return undefined
          }

          // Only show the thread skeleton when hydrating an empty conversation —
          // background polls must not flash the empty/loading state.
          if (get().messages.length === 0) {
            set({ messagesLoading: true, error: undefined }, false, 'loadMessages/start')
          }

          const [messages, error] = await listChatMessages(uid, sessionId, MESSAGES_LIMIT)
          // Thread changed while the request was in flight — discard the result.
          if (get().sessionId !== sessionId) {
            return undefined
          }
          if (error) {
            logger.error('loadMessages', error)
            activeLoadingPrompt = null
            set(
              {
                error,
                messagesLoading: false,
                awaitingReply: false,
                lastAssistantAt: undefined,
                loadingMessages: undefined,
              },
              false,
              'loadMessages/error'
            )
            return undefined
          }

          const serverMessages = messages ?? []
          const prev = get().messages
          const next = reconcileMessages(prev, serverMessages)
          if (messagesEqual(prev, next)) {
            if (get().awaitingReply) {
              const startedAt = get().lastAssistantAt
              if (startedAt && Date.now() - startedAt >= AWAITING_REPLY_TIMEOUT_MS) {
                activeLoadingPrompt = null
                set(
                  {
                    messagesLoading: false,
                    awaitingReply: false,
                    lastAssistantAt: undefined,
                    loadingMessages: undefined,
                    error: 'No reply received. Please try again.',
                  },
                  false,
                  'loadMessages/awaitingTimeout'
                )
                return prev
              }
            }
            if (get().messagesLoading) {
              set({ messagesLoading: false }, false, 'loadMessages/unchanged')
            }
            return prev
          }

          const nextContentfulIdx = findLastContentfulAssistantIndex(next)

          // Only contentful replies clear the typing wait — empty assistant
          // placeholders are hidden in ChatScreen, so clearing early looks like
          // a silent failure.
          const shouldClearAwaiting = hasNewContentfulAssistant(prev, next) && get().awaitingReply

          if (shouldClearAwaiting) {
            const lastAssistant = next[nextContentfulIdx]
            const startedAt = get().lastAssistantAt ?? Date.now()
            const latencyMs = Date.now() - startedAt
            activeLoadingPrompt = null
            set(
              {
                messages: next,
                error: undefined,
                awaitingReply: false,
                lastAssistantAt: undefined,
                loadingMessages: undefined,
                messagesLoading: false,
              },
              false,
              'loadMessages/assistantReceived'
            )
            emitChatReplyReceived(sessionId, lastAssistant?.content, latencyMs)
          } else if (get().awaitingReply) {
            const startedAt = get().lastAssistantAt
            if (startedAt && Date.now() - startedAt >= AWAITING_REPLY_TIMEOUT_MS) {
              activeLoadingPrompt = null
              set(
                {
                  messages: next,
                  messagesLoading: false,
                  awaitingReply: false,
                  lastAssistantAt: undefined,
                  loadingMessages: undefined,
                  error: 'No reply received. Please try again.',
                },
                false,
                'loadMessages/awaitingTimeout'
              )
            } else {
              set({ messages: next, error: undefined, messagesLoading: false }, false, 'loadMessages/success')
            }
          } else {
            set({ messages: next, error: undefined, messagesLoading: false }, false, 'loadMessages/success')
          }

          return next
        },

        requestLoadingMessages: (prompt: string) => {
          const trimmed = prompt.trim()
          if (!trimmed) return

          const uid = getCurrentUID()
          if (!uid) return

          activeLoadingPrompt = trimmed
          // Fire-and-forget — TypingIndicator falls back to the WidgetChatScreen
          // default if the endpoint doesn't respond in time or is unavailable.
          fetchChatLoadingMessages(uid, trimmed)
            .then(([messages, error]) => {
              if (error || !messages?.length) return
              if (activeLoadingPrompt !== trimmed) return
              set({ loadingMessages: messages }, false, 'requestLoadingMessages/success')
            })
            .catch((err) => {
              logger.error('requestLoadingMessages', err)
            })
        },

        loadSessions: async () => {
          const uid = getCurrentUID()
          if (!uid) {
            set({ sessionsError: 'No current UID' }, false, 'loadSessions/noUID')
            return
          }

          set({ sessionsLoading: true, sessionsError: undefined }, false, 'loadSessions/start')

          const [sessions, error] = await listChatSessions(uid, 50)
          if (error) {
            logger.error('loadSessions', error)
            set({ sessionsLoading: false, sessionsError: error }, false, 'loadSessions/error')
            return
          }

          // Sort most-recent first, tolerating missing timestamps.
          const sortKey = (s: ChatSession) =>
            new Date(s.lastMessageAt ?? s.updated ?? s.created ?? 0).getTime()
          const sorted = [...(sessions ?? [])].sort((a, b) => sortKey(b) - sortKey(a))

          set(
            { sessions: sorted, sessionsLoading: false, sessionsError: undefined },
            false,
            'loadSessions/success'
          )

          // Prefetch labels for the most recent sessions only — avoid a
          // fan-out of listChatMessages calls for the full 50-session list.
          void get().loadSessionPreviews(sorted.slice(0, SESSION_PREVIEW_PREFETCH_LIMIT).map((s) => s.id))
        },

        loadSessionPreviews: async (sessionIds: string[]) => {
          const uid = getCurrentUID()
          if (!uid) return

          const uniqueIds = [...new Set(sessionIds.filter(isValidSessionId))]
          if (uniqueIds.length === 0) return

          const existing = get().sessionPreviews
          const missing = uniqueIds
            .filter((id) => existing[id] === undefined)
            .slice(0, SESSION_PREVIEW_PREFETCH_LIMIT)
          if (missing.length === 0) return

          const results = await mapPool(missing, SESSION_PREVIEW_CONCURRENCY, async (sessionId) => {
            const [messages] = await listChatMessages(uid, sessionId, 20)
            return [sessionId, messages ?? []] as const
          })

          set(
            (state) => {
              const next = { ...state.sessionPreviews }
              for (const [sessionId, messages] of results) {
                next[sessionId] = messages
              }
              return { sessionPreviews: next }
            },
            false,
            'loadSessionPreviews/success'
          )
        },

        selectSession: async (sessionId: string) => {
          const trimmed = sessionId?.trim()
          if (!trimmed || trimmed === get().sessionId) {
            return
          }

          // Don't switch threads while a POST is in flight — bumping the epoch
          // would make sendMessage abandon UI updates while the request can still
          // land on the previous session.
          if (get().sending) return

          get().stopPolling()
          stopStreaming()
          activeLoadingPrompt = null
          bumpThreadEpoch()

          set(
            {
              sessionId: trimmed,
              messages: [],
              input: '',
              sending: false,
              awaitingReply: false,
              lastAssistantAt: undefined,
              loadingMessages: undefined,
              error: undefined,
              messagesLoading: true,
              preferNewChat: false,
              streamingAssistantId: undefined,
              streamingSequence: 0,
              canCancelReply: false,
            },
            false,
            'selectSession/switch'
          )

          await get().loadMessages()

          if (isChatUiActive()) {
            get().startPolling()
          }
        },

        resumeLatestSession: async () => {
          if (resumeInFlight) return resumeInFlight

          resumeInFlight = (async () => {
            try {
              if (isValidSessionId(get().sessionId)) {
                await get().loadMessages()
                return
              }

              // User started a new chat — keep the empty draft across remounts.
              if (get().preferNewChat) {
                set({ messages: [], messagesLoading: false }, false, 'resumeLatestSession/preferNew')
                return
              }

              await get().loadSessions()

              // A concurrent sendMessage may have created/bound a session while
              // we were listing — don't clobber that thread with an older one.
              if (isValidSessionId(get().sessionId) || get().preferNewChat || get().sending) {
                return
              }

              const latest = get().sessions[0]
              if (!latest?.id) {
                set({ messages: [], messagesLoading: false }, false, 'resumeLatestSession/empty')
                return
              }

              await get().selectSession(latest.id)
            } finally {
              resumeInFlight = null
            }
          })()

          return resumeInFlight
        },

        newChat: () => {
          // Same as selectSession — wait out an in-flight send so the POST
          // cannot succeed against a thread the UI has already abandoned.
          if (get().sending) return

          if (pollTimer) {
            clearTimeout(pollTimer)
            pollTimer = null
          }
          clearPollSubscriptions()
          stopStreaming()
          activeLoadingPrompt = null
          bumpThreadEpoch()

          set(
            {
              sessionId: undefined,
              messages: [],
              input: '',
              sending: false,
              polling: false,
              pollCount: 0,
              awaitingReply: false,
              lastAssistantAt: undefined,
              loadingMessages: undefined,
              error: undefined,
              messagesLoading: false,
              preferNewChat: true,
              streamingAssistantId: undefined,
              streamingSequence: 0,
              canCancelReply: false,
            },
            false,
            'newChat'
          )
          widgetBridge.emit('chat:reset', {})
        },

        sendMessage: async (text) => {
          const value = (text ?? get().input).trim()
          if (!value) return

          if (isReplyInFlight(get())) return

          if (isChatMessageTooLong(value)) {
            set(
              { error: `Message must be ${MAX_CHAT_MESSAGE_CHARS} characters or fewer` },
              false,
              'sendMessage/tooLong'
            )
            return
          }

          // Wait for mount-time resume so we don't create a new session that
          // resume then overwrites via selectSession.
          if (resumeInFlight) {
            await resumeInFlight
          }

          if (isReplyInFlight(get())) return
          const sendEpoch = threadEpoch
          set(
            {
              sending: true,
              awaitingReply: true,
              error: undefined,
              loadingMessages: undefined,
              lastAssistantAt: Date.now(),
            },
            false,
            'sendMessage/start'
          )
          // Kick off contextual loading messages in parallel with the POST.
          get().requestLoadingMessages(value)

          // Optimistic user message so the UI feels responsive while we
          // wait on ensureSession + POST + first poll.
          // Pass a title candidate from the prompt; the shopper backend only
          // writes it when the session is still blank / on the legacy default.
          const sessionTitle = titleFromPrompt(value)
          const optimisticId = `local-${Date.now()}`
          const optimistic: ChatMessage = {
            id: optimisticId,
            role: 'user',
            content: value,
            createdAt: new Date().toISOString(),
          }
          set(
            (state) => ({
              messages: [...state.messages, optimistic],
              input: text === undefined ? '' : state.input,
            }),
            false,
            'sendMessage/optimistic'
          )

          const isStale = () => sendEpoch !== threadEpoch

          const failSend = (error: string | undefined, action: string) => {
            if (isStale()) return
            activeLoadingPrompt = null
            set(
              (state) => ({
                messages: state.messages.map((m) => (m.id === optimisticId ? { ...m, sendFailed: true } : m)),
                sending: false,
                awaitingReply: false,
                lastAssistantAt: undefined,
                loadingMessages: undefined,
                error,
              }),
              false,
              action
            )
            if (isValidSessionId(get().sessionId) && isChatUiActive()) {
              get().startPolling()
            }
          }

          try {
            get().stopPolling()

            const sessionId = await get().ensureSession()
            if (isStale()) return
            if (!sessionId) {
              failSend(get().error ?? 'Failed to create chat session', 'sendMessage/noSession')
              return
            }

            const uid = getCurrentUID()
            if (!uid) {
              failSend('No current UID', 'sendMessage/noUID')
              return
            }

            const globalID = getCurrentGlobalID()
            const storeID = globalID ? getStoreIDByGlobalID(globalID) : undefined
            const contactID = getContactID()

            const [result, error] = await postChatMessage(
              uid,
              sessionId,
              value,
              storeID,
              sessionTitle,
              contactID
            )
            if (isStale()) return
            if (error) {
              logger.error('sendMessage', error)
              failSend(error, 'sendMessage/postError')
              return
            }

            if (sessionTitle) {
              set(
                (state) => {
                  const sessions = state.sessions.map((s) =>
                    s.id === sessionId && (!s.title || isDefaultShopperSessionTitle(s.title))
                      ? { ...s, title: sessionTitle }
                      : s
                  )
                  const preview = state.sessionPreviews[sessionId] ?? []
                  const hasUserPreview = preview.some((m) => !isAssistantMessage(m))
                  return {
                    sessions,
                    sessionPreviews: hasUserPreview
                      ? state.sessionPreviews
                      : {
                          ...state.sessionPreviews,
                          [sessionId]: [optimistic, ...preview],
                        },
                  }
                },
                false,
                'sendMessage/sessionTitle'
              )
            }

            widgetBridge.emit('chat:message_sent', {
              sessionId,
              message: value,
              storeID,
              contactID,
            })
            track('chat', 'chat_message_sent', {
              length: value.length,
              hasStoreID: Boolean(storeID),
              hasContactID: Boolean(contactID),
            })

            const persistedMessage = result?.message
            if (persistedMessage) {
              set(
                (state) => ({
                  messages: state.messages.map((m) =>
                    m.id === optimisticId
                      ? { ...persistedMessage, id: optimisticId, sendFailed: undefined }
                      : m
                  ),
                }),
                false,
                'sendMessage/persisted'
              )
            }

            // Only refresh/stream/poll the thread this send targeted. If the
            // user switched away mid-flight, leave the new active thread alone.
            if (get().sessionId !== sessionId) return

            const streamHandle = result?.stream
              ? {
                  ...result.stream,
                  ...(result.job?.id && !result.stream.jobID ? { jobID: result.job.id } : {}),
                }
              : undefined
            if (streamHandle?.wsAuthTicket?.trim()) {
              // Streaming path: hand off to the WS. startStream is responsible
              // for eventually calling loadMessages + startPolling on finalize.
              // Free the send lock now so the input isn't blocked while tokens
              // stream in.
              set({ sending: false }, false, 'sendMessage/stream')
              get().startStream(streamHandle, sendEpoch)
              return
            }

            // Fallback: no stream on the response (older backend or broker
            // unavailable) — original polling path.
            await get().loadMessages()
            if (isStale()) return
            get().startPolling()
            set({ sending: false }, false, 'sendMessage/success')
          } catch (err) {
            if (isStale()) return
            const message = err instanceof Error ? err.message : 'Failed to send message'
            logger.error('sendMessage/catch', message)
            failSend(message, 'sendMessage/catch')
          }
        },

        retryFailedMessage: async (messageId) => {
          if (!messageId || isReplyInFlight(get())) return
          const failed = get().messages.find((m) => m.id === messageId && m.sendFailed)
          if (!failed?.content?.trim()) return

          const content = failed.content.trim()
          set(
            (state) => ({
              messages: state.messages.filter((m) => m.id !== messageId),
              error: undefined,
            }),
            false,
            'retryFailedMessage/clear'
          )
          await get().sendMessage(content)
        },

        startPolling: () => {
          get().stopPolling()

          if (!isChatUiActive() || !isValidSessionId(get().sessionId)) {
            return
          }

          // Poll only while Chat is the active step and (in floating mode) the
          // widget is open. Soft-idle backoff keeps long-open tabs cheap.
          const scheduleNext = () => {
            const { pollCount } = get()
            const interval = pollCount >= IDLE_POLL_AFTER ? IDLE_POLL_INTERVAL_MS : POLL_INTERVAL_MS
            pollTimer = setTimeout(async () => {
              if (!isChatUiActive()) {
                set({ polling: false }, false, 'startPolling/pausedWhileInactive')
                return
              }
              set((state) => ({ pollCount: state.pollCount + 1 }), false, 'startPolling/tick')
              try {
                await get().loadMessages()
              } catch (err) {
                logger.error('startPolling/tick', err)
              }
              if (get().polling) scheduleNext()
            }, interval)
          }

          const pausePolling = (action: string) => {
            if (pollTimer) {
              clearTimeout(pollTimer)
              pollTimer = null
            }
            set({ polling: false }, false, action)
          }

          const resumePolling = (action: string) => {
            if (!isValidSessionId(get().sessionId) || !isChatUiActive()) return
            set({ polling: true, pollCount: 0 }, false, action)
            scheduleNext()
          }

          set({ polling: true, pollCount: 0 }, false, 'startPolling/start')

          clearPollSubscriptions()
          pollUnsubscribers.push(
            useWidgetStore.subscribe((state, prev) => {
              if (!state.isFloatingWidgetMode) return
              if (state.floatingWidgetOpen === prev.floatingWidgetOpen) return
              if (!state.floatingWidgetOpen) {
                pausePolling('startPolling/pausedOnClose')
              } else {
                resumePolling('startPolling/resumeOnOpen')
              }
            })
          )
          pollUnsubscribers.push(
            useFlowState.subscribe((state, prev) => {
              if (state.currentStep === prev.currentStep) return
              if (state.currentStep !== WidgetStep.Chat) {
                pausePolling('startPolling/pausedOffChat')
              } else {
                resumePolling('startPolling/resumeOnChat')
              }
            })
          )

          scheduleNext()
        },

        stopPolling: () => {
          if (pollTimer) {
            clearTimeout(pollTimer)
            pollTimer = null
          }
          clearPollSubscriptions()
          if (get().polling) {
            set({ polling: false }, false, 'stopPolling')
          }
        },

        stopStreaming: () => {
          stopStreaming()
        },

        cancelStreaming: () => {
          if (!streamConnection) return
          streamConnection.cancel()
        },

        startStream: (stream, sendEpoch) => {
          if (sendEpoch !== threadEpoch) return
          if (!stream?.wsAuthTicket?.trim()) return

          // Close the prior socket without dropping its bubble. A follow-up
          // send should not erase the previous turn's streamed text.
          closeStreamConnection()
          streamEpoch += 1
          const thisStreamEpoch = streamEpoch

          const isStale = () => sendEpoch !== threadEpoch
          const isCurrentStream = () => thisStreamEpoch === streamEpoch && !isStale()

          // A placeholder-id per stream so late deltas from a prior stream
          // can't accidentally target a fresh assistant bubble.
          const streamAssistantId = `${STREAM_PLACEHOLDER_ID_PREFIX}${sendEpoch}-${Date.now()}`

          set(
            { streamingSequence: 0, streamingAssistantId: undefined, canCancelReply: false },
            false,
            'startStream/begin'
          )

          let bumpStreamWatchdog = () => {}

          const appendDelta = (delta: string, sequence: number) => {
            if (isStale() || !delta) return
            const applied = get().streamingSequence
            if (sequence > 0 && sequence <= applied) return

            const currentId = get().streamingAssistantId
            if (!currentId) {
              set(
                (state) => ({
                  messages: [
                    ...state.messages,
                    {
                      id: streamAssistantId,
                      role: 'assistant',
                      content: delta,
                      createdAt: new Date().toISOString(),
                    },
                  ],
                  streamingAssistantId: streamAssistantId,
                  streamingSequence: sequence > 0 ? sequence : state.streamingSequence,
                  awaitingReply: false,
                  loadingMessages: undefined,
                }),
                false,
                'startStream/firstDelta'
              )
              activeLoadingPrompt = null
              bumpStreamWatchdog()
              return
            }

            set(
              (state) => ({
                messages: state.messages.map((m) =>
                  m.id === currentId ? { ...m, content: (m.content ?? '') + delta } : m
                ),
                streamingSequence: sequence > 0 ? sequence : state.streamingSequence,
              }),
              false,
              'startStream/appendDelta'
            )
            bumpStreamWatchdog()
          }

          const finalize = async (
            outcome: 'completed' | 'failed' | 'cancelled' | 'error',
            event?: ChatStreamEvent
          ) => {
            closeStreamConnection()
            if (!isCurrentStream()) {
              return
            }
            set({ canCancelReply: false }, false, 'startStream/finalize/socketClosed')

            const startedAt = get().lastAssistantAt
            const latencyMs = startedAt ? Date.now() - startedAt : undefined
            // First token already cleared awaitingReply; emit here so streamed
            // replies still fire chat:reply_received. The polling path emits
            // from loadMessages while the flag is still true.
            const shouldEmitReplyReceived = outcome === 'completed' && !get().awaitingReply

            const sessionId = get().sessionId
            const uid = getCurrentUID()
            if (isValidSessionId(sessionId) && uid && outcome !== 'cancelled') {
              try {
                const [messages, error] = await listChatMessages(uid, sessionId, MESSAGES_LIMIT)
                if (!isCurrentStream()) return
                if (error) {
                  logger.error('startStream/finalize/loadMessages', error)
                } else {
                  const prev = get().messages
                  const next = reconcileMessages(prev, messages ?? [])
                  set(
                    {
                      messages: next,
                      error: undefined,
                      messagesLoading: false,
                    },
                    false,
                    'startStream/finalize/hydrated'
                  )
                }
              } catch (err) {
                logger.error('startStream/finalize/loadMessages', err)
              }
            }

            if (!isCurrentStream()) return

            const placeholderId = get().streamingAssistantId
            // Failed/cancelled jobs never persist an assistant row. Drop the
            // partial stream bubble so it cannot sit beside the next turn.
            const shouldDropPlaceholder = outcome === 'failed' || outcome === 'cancelled'
            set(
              (state) => ({
                messages:
                  shouldDropPlaceholder && placeholderId
                    ? state.messages.filter((m) => m.id !== placeholderId)
                    : state.messages,
                streamingAssistantId: undefined,
                streamingSequence: 0,
                canCancelReply: false,
              }),
              false,
              'startStream/finalize/releaseStream'
            )

            const canonicalMessages = get().messages.filter((m) => !isStreamPlaceholderMessage(m))
            const currentTurnHasReply = hasAssistantReplyAfterLastUser(canonicalMessages)

            if (outcome === 'failed') {
              if (!currentTurnHasReply) {
                set(
                  {
                    awaitingReply: false,
                    lastAssistantAt: undefined,
                    loadingMessages: undefined,
                    error: event
                      ? errorFromFailedStreamEvent(event)
                      : 'Streaming reply failed. Please try again.',
                  },
                  false,
                  'startStream/finalize/failed'
                )
              }
            } else if (outcome === 'cancelled') {
              set(
                {
                  awaitingReply: false,
                  lastAssistantAt: undefined,
                  loadingMessages: undefined,
                  error: undefined,
                },
                false,
                'startStream/finalize/cancelled'
              )
            } else if (outcome === 'completed') {
              if (shouldEmitReplyReceived && isValidSessionId(sessionId)) {
                const lastIdx = findLastContentfulAssistantIndex(get().messages)
                emitChatReplyReceived(
                  sessionId,
                  lastIdx >= 0 ? get().messages[lastIdx]?.content : undefined,
                  latencyMs ?? 0
                )
              }
            } else if (currentTurnHasReply && get().awaitingReply && isValidSessionId(sessionId)) {
              // Transport dropped, but the canonical fetch already has the
              // reply. Emit here so we don't rely on a later poll.
              const lastIdx = findLastContentfulAssistantIndex(get().messages)
              emitChatReplyReceived(
                sessionId,
                lastIdx >= 0 ? get().messages[lastIdx]?.content : undefined,
                latencyMs ?? 0
              )
            }

            // Silent HTTP fallback for socket/timeout/clean-close: keep
            // awaitingReply so a later poll still emits chat:reply_received.
            // Refresh lastAssistantAt so loadMessages' 90s timeout does not
            // immediately fire after a hung-socket watchdog.
            if (outcome === 'error' && get().awaitingReply && !currentTurnHasReply) {
              set({ lastAssistantAt: Date.now() }, false, 'startStream/finalize/fallbackWait')
            } else if (outcome !== 'error' || currentTurnHasReply) {
              if (get().awaitingReply || get().lastAssistantAt) {
                set(
                  {
                    awaitingReply: false,
                    lastAssistantAt: undefined,
                    loadingMessages: undefined,
                  },
                  false,
                  'startStream/finalize/clearAwaiting'
                )
              }
            }

            widgetBridge.emit('chat:stream_finalized', {
              sessionId: get().sessionId,
              outcome,
              latencyMs,
            })

            if (isChatUiActive() && !get().polling) {
              get().startPolling()
            }
          }

          // Local sentinel so async socket handlers can tell whether they
          // still belong to the currently active stream. Cleared by
          // closeStreamConnection; a new startStream mints a fresh sentinel.
          const ownedConnectionRef: { current: ChatStreamConnection | null } = { current: null }

          // Stop while CONNECTING (or a failed upgrade after cancel is queued)
          // typically fires onError before onClose. finalize() then close()s
          // the socket, which suppresses onClose. Treat those drops as cancel
          // so wait state clears and the partial bubble is dropped.
          const finalizeDroppedStream = (logError: () => void) => {
            if (ownedConnectionRef.current?.didCancel()) {
              void finalize('cancelled')
              return
            }
            logError()
            void finalize('error')
          }

          bumpStreamWatchdog = () => {
            clearStreamWatchdog()
            streamWatchdog = setTimeout(() => {
              streamWatchdog = null
              if (!isCurrentStream()) return
              finalizeDroppedStream(() => {
                logger.error('startStream/timeout', 'Chat stream timed out waiting for events')
              })
            }, AWAITING_REPLY_TIMEOUT_MS)
          }
          bumpStreamWatchdog()

          try {
            const isOwnedConnection = () =>
              ownedConnectionRef.current !== null && streamConnection === ownedConnectionRef.current

            const connection = openChatStream({
              stream,
              onEvent: (event) => {
                if (!isOwnedConnection() || isStale()) return
                switch (event.type) {
                  case 'token':
                    appendDelta(event.delta ?? '', event.sequence ?? 0)
                    return
                  case 'activity':
                  case 'thinking':
                    bumpStreamWatchdog()
                    return
                  case 'completed':
                    void finalize('completed', event)
                    return
                  case 'failed':
                    void finalize('failed', event)
                    return
                  case 'cancelled':
                    void finalize('cancelled', event)
                    return
                }
              },
              onError: (err) => {
                if (!isOwnedConnection()) return
                finalizeDroppedStream(() => {
                  logger.error('startStream/onError', err)
                })
              },
              onClose: (info) => {
                // Client close() marks the socket closed first, so this only
                // runs for a broker/network close. Finalize as `error` (silent
                // HTTP fallback), not `failed`, so a transient drop does not
                // show a banner or skip chat:reply_received on the later poll.
                if (!isOwnedConnection()) return
                finalizeDroppedStream(() => {
                  logger.error(
                    'startStream/onClose',
                    `socket closed without terminal event (wasClean=${info.wasClean}, code=${info.code})`
                  )
                })
              },
            })
            ownedConnectionRef.current = connection
            streamConnection = connection
            set({ canCancelReply: true }, false, 'startStream/opened')

            // Tear down the WS as soon as the widget closes or the user leaves
            // the Chat step. The final assistant reply is still persisted
            // server-side; loadMessages will pick it up when the widget reopens.
            clearStreamSubscriptions()
            streamUnsubscribers.push(
              useWidgetStore.subscribe((state, prev) => {
                if (!state.isFloatingWidgetMode) return
                if (state.floatingWidgetOpen === prev.floatingWidgetOpen) return
                if (!state.floatingWidgetOpen) stopStreaming()
              })
            )
            streamUnsubscribers.push(
              useFlowState.subscribe((state, prev) => {
                if (state.currentStep === prev.currentStep) return
                if (state.currentStep !== WidgetStep.Chat) stopStreaming()
              })
            )
          } catch (err) {
            logger.error('startStream/open', err)
            void finalize('error')
          }
        },

        reset: () => {
          if (pollTimer) {
            clearTimeout(pollTimer)
            pollTimer = null
          }
          clearPollSubscriptions()
          stopStreaming()
          activeLoadingPrompt = null
          bumpThreadEpoch()
          set({ ...initialState }, false, 'reset')
          widgetBridge.emit('chat:reset', {})
        },
      }),
      createPersistOptions<ChatStore, 'sessionId' | 'messages' | 'preferNewChat' | 'input'>({
        name: 'chat',
        pick: ['sessionId', 'messages', 'preferNewChat', 'input'],
        storageType: 'session',
        transform: (partial) => ({
          ...partial,
          // Never persist the in-flight streaming placeholder — a reload
          // would resurrect a partial reply that no longer has a socket
          // pushing tokens into it.
          messages: partial.messages.filter((m) => !isStreamPlaceholderMessage(m)),
        }),
      })
    ),
    { name: 'ChatStore', enabled: process.env.NODE_ENV === 'development' }
  )
)

/**
 * Convenience getter used by non-hook consumers (bridge/embed).
 */
export function sendChatMessage(text: string): Promise<void> {
  return useChatStore.getState().sendMessage(text)
}
